import React, { useState } from "react";
import "./css/loan_type.scss";

import StandardTemplate from "../../templates/standard_template/standard_template";

import LoanTypeMainPage from "../../components/loan_types/loan_type_main_page";
import EditLoanPage from "../../components/loan_types/edit_loan";

import { YES } from "../../constants/yes_no";

import { useDocument } from "../../components/loan_tools/loan_tool";
import { LOAN_TYPE } from "../../constants/navigations";
import { default_loan } from "../../schema/loan";

// A brand-new loan slot starts from the schema default (rate 0, base fee 0, etc.);
// only the shared loan-TYPE fields are synced across loans. Rate, Base Fee / Credit,
// and other pricing stay independent per loan.
function newSameTypeLoan(source) {
  const loan = JSON.parse(JSON.stringify(default_loan));
  sameLoanFields.forEach(field => (loan[field] = source[field]));
  return loan;
}

const sameLoanFields = [
  "type",
  "term",
  "arm_caps",
  "arm_margin",
  "arm_index_type",
  "arm_index_percent",
  "purpose",
  "jumbo_months_of_reserves",
  "jumbo_reserves_for_other_properties",
  "jumbo_arm_months_of_reserves",
  "buydown_type",
  "buydown_paid_by",
  "buydown_disclose_product",
  "buydown_step_months"
];

export default function LoanTypePage() {
  const [document, setDocument] = useDocument();
  const [activeView, setActiveView] = useState("MAIN_PAGE");
  const [loanIndex, setLoanIndex] = useState();
  const { loans } = document;

  function showMainPage() {
    setActiveView("MAIN_PAGE");
    setLoanIndex(null);
  }

  function showEditLoan(loanIndex) {
    setActiveView("EDIT_LOAN");
    setLoanIndex(loanIndex);
  }

  function onChangeAllLoanTypesTheSame(updatedState) {
    const applicant = {
      ...document.applicant,
      ...updatedState
    };
    const loans = [...document.loans];
    if (applicant.all_loan_types_the_same === YES && loans.length > 0) {
      const firstLoan = loans[0];
      for (let i = 1; i < 5; i++) {
        if (loans[i]) {
          // Existing loan: sync only the shared type fields, keep its own pricing.
          loans[i] = { ...loans[i] };
          sameLoanFields.forEach(field => (loans[i][field] = firstLoan[field]));
        } else {
          // New slot: default pricing (rate 0, base fee 0) + shared type fields.
          loans[i] = newSameTypeLoan(firstLoan);
        }
      }
    }
    // Selecting "No" only stops type-syncing going forward. It must NEVER delete
    // a loan or reset any pricing (LE Price Eng / Opt) or closing-cost data —
    // Loans 2-5 stay exactly as they are. If the LO wants a loan blank, they use
    // that loan's own Delete button, a deliberate per-loan action.
    setDocument({ ...document, applicant, loans });
  }

  function onChangeLoan(loan) {
    if (document.applicant.all_loan_types_the_same === YES) {
      const existing = document.loans;
      const loans = [];
      for (let i = 0; i < 5; i++) {
        if (i === loanIndex) {
          loans.push(loan);
        } else if (existing[i]) {
          // Keep each other loan's own pricing; sync only the shared type fields.
          const other = { ...existing[i] };
          sameLoanFields.forEach(field => (other[field] = loan[field]));
          loans.push(other);
        } else {
          loans.push(newSameTypeLoan(loan));
        }
      }
      setDocument({ ...document, loans });
    } else {
      const loans = [...document.loans];
      loans[loanIndex] = loan;
      setDocument({ ...document, loans });
    }
  }

  function onDeleteLoan(loanIndex) {
    if (document.applicant.all_loan_types_the_same === YES) {
      // The "Loans 1-5" column represents every loan, so this clears them all
      // — including each loan's own rate and pricing. Confirm first.
      const count = document.loans.length;
      if (
        !window.confirm(
          `Delete Loans 1-5? This removes all ${count} loan${
            count === 1 ? "" : "s"
          } and their rates and pricing. This can't be undone.`
        )
      ) {
        return;
      }
      const loans = [];
      setDocument({ ...document, loans });
    } else {
      if (
        !window.confirm(
          `Delete Loan ${loanIndex + 1}? Its rate and pricing will be lost. This can't be undone.`
        )
      ) {
        return;
      }
      const loans = [...document.loans];
      loans.splice(loanIndex, 1);
      setDocument({ ...document, loans });
    }
  }

  return (
    <StandardTemplate
      active={LOAN_TYPE}
      isDisableNavSave={activeView !== "MAIN_PAGE"}
    >
      <div className="loan-type">
        {activeView === "MAIN_PAGE" && (
          <LoanTypeMainPage
            showEditLoan={showEditLoan}
            onChangeAllLoanTypesTheSame={onChangeAllLoanTypesTheSame}
            onDeleteLoan={onDeleteLoan}
          />
        )}
        {activeView === "EDIT_LOAN" && (
          <EditLoanPage
            loan={loans[loanIndex]}
            loanIndex={loanIndex}
            showMainPage={showMainPage}
            onChangeLoan={onChangeLoan}
          />
        )}
      </div>
    </StandardTemplate>
  );
}
